/**
 * Context sync API client interface.
 *
 * Abstracts HTTP calls for context-driven sync:
 * - List sync: fetch metadata for all entities of a type the user can access
 * - Children sync: fetch children of a specific entity
 * - Push: send local changes to server
 */

export interface EntitySummary {
  entityType: string
  entityId: string
  version: number
  data: Record<string, unknown>
}

export interface ContextSyncResponse {
  entities: EntitySummary[]
}

export interface PushChange {
  syncEventId: string
  entityType: string
  entityId: string
  operation: 'create' | 'update' | 'delete'
  data: Record<string, unknown>
  expectedVersion: number
  force?: boolean
}

export interface PushResult {
  syncEventId: string
  success: boolean
  newVersion?: number
  error?: string
  conflict?: {
    serverVersion: number
    serverData: Record<string, unknown>
  }
  denied?: {
    reason: string
    entityType: string
    entityId: string
    parentType?: string
    parentId?: string
  }
}

export interface PushRequest {
  changes: PushChange[]
}

export interface PushResponse {
  results: PushResult[]
}

export interface ContextSyncApi {
  fetchList(entityType: string): Promise<ContextSyncResponse>
  fetchChildren(entityType: string, entityId: string): Promise<ContextSyncResponse>
  push(request: PushRequest): Promise<PushResponse>
}

export function createContextSyncApi(
  baseUrl: string,
  getAccessToken: () => Promise<string>
): ContextSyncApi {
  async function authHeaders(): Promise<Record<string, string>> {
    const token = await getAccessToken()
    return {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
    }
  }

  return {
    async fetchList(entityType) {
      const headers = await authHeaders()
      const res = await fetch(`${baseUrl}/api/sync/${entityType}`, { headers })
      if (!res.ok) throw new Error(`Sync fetch failed: ${res.status}`)
      return res.json()
    },

    async fetchChildren(entityType, entityId) {
      const headers = await authHeaders()
      const res = await fetch(`${baseUrl}/api/sync/entity/${entityType}/${entityId}/children`, {
        headers,
      })
      if (!res.ok) throw new Error(`Sync fetch children failed: ${res.status}`)
      return res.json()
    },

    async push(request) {
      const headers = await authHeaders()
      const res = await fetch(`${baseUrl}/api/sync/push`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      })
      if (!res.ok) {
        const body = await res.text()
        let detail = ''
        try {
          detail = JSON.parse(body).detail ?? ''
        } catch {
          /* ignore */
        }
        throw new Error(`Push failed: ${res.status}${detail ? ` — ${detail}` : ''}`)
      }
      return res.json()
    },
  }
}
